import { ActionType } from 'typesafe-actions';
import { call, put, select } from 'redux-saga/effects';

import { balanceSelector, totalBetSelector } from 'core/widgets/User';

import {
  makeBetsAreaBetAction,
  makeRaceTrackBetAction,
  makeStraightBetAction,
  placeBetAction,
} from '../actions';
import { betsAreaSelector, placedBetSelector } from '../selectors';
import { BetsLimitsType, IBets, IClientBet, IMakeBet } from 'widgets/Roulette/types';

import { isEmpty, pathOr } from 'ramda';
import {
  calculateNeighboursBet,
  getBetPath,
  getStraightWithNeighbours,
  mergeWithPresent,
  sumBets,
  caclulateVoisinsSum,
} from 'widgets/Roulette/helpers';
import { BetsTypes, sideBets, sideBetsSet } from 'widgets/Roulette/const';
import { activeChipSelector } from '../../ActiveChip';
import { ChipType } from '../../ActiveChip/types';
import { refreshBetsSaga } from './refreshBetsSaga';
import { tableBetLimitsSelector } from '../../TableSettings';
import { addToastAction } from '../../Toast/actions';
import { ToastTypes } from 'types/toast';
import { betMessages } from 'constants/toastMessages';
import { validateForReducer } from '../utils/normalize';

export function* placeBetSaga({ payload: { bets, double } }: ActionType<typeof placeBetAction>) {
  const balance: number | null = yield select(balanceSelector);
  const tableLimits: BetsLimitsType = yield select(tableBetLimitsSelector);
  const currentBets: IBets = yield select(placedBetSelector);
  const activeChip: ChipType = yield select(activeChipSelector);
  const placedBetSumBeforeNewBet: number = yield select(totalBetSelector);

  if (!balance || !tableLimits) {
    return;
  }

  const { past, present, future } = currentBets;

  if (placedBetSumBeforeNewBet >= tableLimits.total.max) {
    yield put(addToastAction({ type: ToastTypes.ERROR, value: betMessages.MAX_BET_REACHED }));
    return;
  }

  const hasMaxLimitBet = bets.find((betInfo) => {
    const { type, currentKey } = getBetPath<IMakeBet>(betInfo);
    const betValue = pathOr(0, [type, currentKey, 'bet'], present);
    if (!tableLimits[type]) return false;
    return betValue >= tableLimits[type].max;
  });

  if (hasMaxLimitBet) {
    yield put(addToastAction({ type: ToastTypes.ERROR, value: betMessages.MAX_BET_REACHED }));
    return;
  }

  const isAutoConfirmOn: boolean = true;
  const amount = activeChip.value;

  const updatedPast = isEmpty(present) ? past : [...past, present];

  let shouldShowMaxBetToast = false;

  const betsResult = bets.reduce(
    (acc: IBets, betinfo: IMakeBet) => {
      const { type, currentKey } = getBetPath<IMakeBet>(betinfo);
      const betValue = pathOr(0, [type, currentKey, 'bet'], present);
      const { chips = 1 } = betinfo;

      const isRaceTrackBet = sideBets.has(type);
      const isOtherStraightBet =
        type === BetsTypes.historyStraight || type === BetsTypes.hotColdStraight;

      const remainingTotalAmountBet = tableLimits.total.max - placedBetSumBeforeNewBet;
      const isNewValueExceededTotal = amount * chips > remainingTotalAmountBet;

      // @ts-ignore
      const placedStraightAmount = present[BetsTypes.straight]?.[currentKey]?.bet || 0;
      const remainingStraightAmountBet = tableLimits[BetsTypes.straight].max - placedStraightAmount;
      const isNewValueExceededStraight =
        placedStraightAmount + amount * chips > tableLimits[BetsTypes.straight].max;

      const getAmount = () => {
        if (double) {
          return betValue;
        }
        if (isOtherStraightBet && isNewValueExceededStraight) {
          return remainingStraightAmountBet;
        }
        if (!isRaceTrackBet && isNewValueExceededTotal) {
          shouldShowMaxBetToast = true;
          return remainingTotalAmountBet;
        }
        return amount * chips;
      };

      const currentAmount = getAmount();
      const currentBet = betValue ? betValue + currentAmount : currentAmount;
      const currentConfirmed = pathOr(0, [type, currentKey, 'confirmed'], present);

      return {
        ...acc,
        present: {
          ...acc.present,
          [type]: {
            // @ts-ignore
            ...acc.present[type],
            [currentKey]: {
              ...betinfo,
              bet: currentBet,
              confirmed: isAutoConfirmOn ? currentBet : currentConfirmed,
            },
          },
        },
      };
    },
    { ...currentBets, past: updatedPast, present, future },
  );

  if (shouldShowMaxBetToast) {
    yield put(addToastAction({ type: ToastTypes.ERROR, value: betMessages.MAX_BET_REACHED }));
  }

  const betsWithLimits: IClientBet[] = yield call(
    validateForReducer,
    betsResult.present,
    tableLimits,
  );

  const isVoisins = bets.find((betInfo) => {
    const { type } = getBetPath<IMakeBet>(betInfo);
    return type === BetsTypes.voisins;
  });

  const validatedBets = isVoisins
    ? caclulateVoisinsSum(currentBets, betsWithLimits)
    : betsWithLimits;

  const totalBetsSum = sumBets(validatedBets);

  if (totalBetsSum > tableLimits.total.max) {
    yield put(addToastAction({ type: ToastTypes.ERROR, value: betMessages.MAX_BET_REACHED }));
    return;
  }

  if (totalBetsSum > balance) {
    yield put(addToastAction({ type: ToastTypes.ERROR, value: betMessages.LOW_BALANCE }));
    return;
  }

  yield refreshBetsSaga({ ...betsResult, present: validatedBets });
}

export function* makeBetsAreaBetSaga({
  payload: { bets },
}: ActionType<typeof makeBetsAreaBetAction>) {
  yield put(placeBetAction({ bets }));
}

export function* makeRaceTrackBetSaga({ payload }: ActionType<typeof makeRaceTrackBetAction>) {
  const { neighboursCount, bets: currentBets } = yield select(betsAreaSelector);
  if (currentBets) {
    const { present } = currentBets;
    const { type } = payload;

    if (type === BetsTypes.raceTrackStraight) {
      const betsWithNeighbours = mergeWithPresent(
        [
          ...getStraightWithNeighbours(payload, neighboursCount),
          ...getStraightWithNeighbours(payload, neighboursCount, BetsTypes.straight),
        ],
        present,
      );
      yield put(placeBetAction({ bets: betsWithNeighbours }));
    }

    if (sideBets.has(type)) {
      const betsWithChipsCount = calculateNeighboursBet(payload, present);
      const betsWithPresent = mergeWithPresent(
        [...sideBetsSet[type], ...betsWithChipsCount],
        present,
      );

      yield put(placeBetAction({ bets: betsWithPresent }));
    }
  }
}

export function* makeStraightBetSaga({ payload }: ActionType<typeof makeStraightBetAction>) {
  const { bets: currentBets } = yield select(betsAreaSelector);
  if (currentBets) {
    const { present } = currentBets;
    const straight = {
      // @ts-ignore
      label: payload.label,
      selector: payload.selector,
      type: BetsTypes.straight,
    };
    const betsMerged = mergeWithPresent([payload, straight], present);

    yield put(placeBetAction({ bets: betsMerged }));
  }
}
